import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Modal — spec'd in handoff/COMPONENT_SPECS.md.
 *
 *   <Modal open={open} onOpenChange={setOpen} title="Cancel service" description="…">
 *     <ModalBody>…</ModalBody>
 *     <ModalFooter>
 *       <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
 *       <Button variant="destructive" onClick={confirm}>Cancel service</Button>
 *     </ModalFooter>
 *   </Modal>
 *
 * Closes on Esc and overlay click. Width capped at 420px (mobile-friendly).
 */

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Width preset — default 420, wide 640. */
  size?: 'default' | 'wide';
}

const SIZE: Record<NonNullable<ModalProps['size']>, string> = {
  default: 'w-[420px] max-w-[92vw]',
  wide: 'w-[640px] max-w-[92vw]',
};

function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  size = 'default',
}: ModalProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            // z-60, not z-30: the cookie banner is fixed at z-50, so every
            // modal in the system opened UNDERNEATH it — including the ones
            // that confirm irreversible actions. Layer order is
            // banner (50) < modal (60) < toast (70), so a toast raised by a
            // modal action is still readable while the modal is open.
            'fixed inset-0 z-[60] bg-ink/70 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[60] -translate-x-1/2 -translate-y-1/2',
            'flex max-h-[90vh] flex-col overflow-hidden',
            'rounded-md border border-border bg-card text-card-foreground shadow-modal outline-none',
            SIZE[size],
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            className
          )}
        >
          <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-6 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-base font-semibold text-foreground">
                {title}
              </DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="mt-1 text-sm text-muted-foreground">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              aria-label="Close"
              className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <X className="size-4" aria-hidden />
            </DialogPrimitive.Close>
          </header>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const ModalBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'min-h-0 flex-1 overflow-y-auto px-6 py-4 text-sm text-foreground',
        className
      )}
      {...props}
    />
  )
);
ModalBody.displayName = 'ModalBody';

const ModalFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex shrink-0 items-center justify-end gap-2 border-t border-border bg-muted px-6 py-3',
        className
      )}
      {...props}
    />
  )
);
ModalFooter.displayName = 'ModalFooter';

export { Modal, ModalBody, ModalFooter };
export type { ModalProps };
